import { useMediaQuery } from 'react-responsive';
import './index.scss';
import '@/assets/scss/global.scss';
import DisplayText from '../../components/DisplayText';

const contents = [
  { num: '01', label: 'Abstract', id: 'abstract' },
  { num: '02', label: 'Motivation', id: 'motivation' },
  { num: '03', label: 'Specification', id: 'specification' },
  { num: '04', label: 'Example Flow', id: 'example-flow' },
  { num: '05', label: 'Rationale', id: 'rationale' },
  { num: '06', label: 'Security', id: 'security' },
];

const ERC = () => {
  const isDesktop = useMediaQuery({ minWidth: 769 });

  const fullText = 'Pre-execution zero-knowledge verification for agent-mediated payments';

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="erc-page">
      {/* Hero Section */}
      <div className="erc-hero relative overflow-hidden">


        <div className={`w-1340 relative z-10 ${isDesktop ? 'pt-[180px] pb-[120px]' : 'pt-[120px] pb-[80px]'}`}>
          {/* Watermark */}
          <div className="erc-watermark" aria-hidden="true">8150</div>
          <div className="erc-tag">ERC-8150</div>
          <DisplayText fullText={fullText} />
          <p className={`erc-hero-desc ${isDesktop ? 'max-w-[736px]' : 'max-w-[100%]'}`}>
            A standard for verifying that an autonomous agent is authorized,
            solvent, and acting within mandate - before any transaction
            settles on chain.
          </p>
          <div className="erc-btn-group">
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              className="erc-btn view-btn erc-btn-outline"
            >
              View on Github
            </a>
            <a
              href="https://eips.ethereum.org"
              target="_blank"
              rel="noreferrer"
              className="get-touch-btn erc-demo-btn"
            >
              <span className="get-touch-btn-text text-[16px]">Read on EIP. Tools</span>
              <span className="get-touch-mask" />
            </a>
            <a
              href="https://form.typeform.com/to/IjCOn4nY"
              target="_blank"
              rel="noreferrer"
              className="get-touch-btn erc-demo-btn"
            >
              <span className="get-touch-btn-text text-[16px]">Talk to the Authors</span>
              <span className="get-touch-mask" />
            </a>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={`w-1340 erc-content-section ${isDesktop ? 'flex pt-[80px] pb-[120px]' : 'pt-[60px] pb-[80px]'}`}>
        {/* Left: Contents sidebar */}
        <div className={`erc-sidebar ${isDesktop ? 'w-[260px] mr-[200px] shrink-0' : 'mb-[48px]'}`}>
          <div className="erc-sidebar-title">Contents</div>
          <div className="erc-sidebar-list">
            {contents.map((item) => (
              <div key={item.num}>
                <button
                  type="button"
                  className="erc-sidebar-item"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="erc-sidebar-num">{item.num}</span>
                  <span className="erc-sidebar-label">{item.label}</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Content */}
        <div className="erc-content flex-1">
          {/* 01 Abstract */}
          <div className="erc-section" id="abstract">
            <div className="erc-section-tag">01 Abstract</div>
            <h2 className="erc-section-title">
              A verification step between intent and settlement.
            </h2>
            <p className="erc-section-desc">
              ERC-8150 defines a standard interface for{' '}
              <strong>pre-execution zero-knowledge verification</strong> of
              payments initiated by autonomous agents. It lets a payment
              processor, wallet, or rollup confirm - without revealing private
              mandate data - that an agent has been properly authorized, has
              sufficient solvency, and is operating within its stated intent
              bounds.
            </p>
            <p className="erc-section-desc">
              In plain terms: a signature proves an agent wanted to do
              something. ERC-8150 proves that the thing they want to do is
              valid, before anyone executes it.
            </p>
          </div>
          {/* 02 */}
          <div className="erc-section" id="motivation">
            <div className="erc-section-tag">02 Motivation</div>
            <h2 className="erc-section-title">
              Signatures aren't enough for autonomous capital.
            </h2>
            <p className="erc-section-desc">
              As AI agents begin to transact on behalf of users, businesses,
              and treasuries, the trust model that underpins today's signed
              transactions - "whoever holds the key is authorized" - breaks down.
              Agents hold ephemeral keys, make thousands of micro-decisions
              per minute, and can be coaxed into actions their principals never intended.
            </p>
            <div className="erc-section-desc">
              ERC-8150 introduces a <strong>verification layer</strong> that sits
              between the agent's signed intent and the settlement rail.
              It turns three core properties into verifiable claims:
            </div>
            <div>
              <p className="erc-section-desc flex items-center">
                <span className={`dot`}/><strong>Authorization</strong>&nbsp;- the agent has a mandate from its principal covering this action.
              </p>
              <p className="erc-section-desc">
                <span className={`dot`}/><strong>Solvency</strong> - the principal has funds to back the action at the moment of execution.
              </p>
              <p className="erc-section-desc">
                <span className={`dot`}/><strong>Intent-conformance</strong> - the action falls within the declared intent (counterparties, limits, time windows).
              </p>
            </div>
          </div>

          {/* 03 */}
          <div className="erc-section" id="specification">
            <div className="erc-section-tag">03 Specification</div>
            <h2 className="erc-section-title">
              The verifier interface.
            </h2>
            <p className="erc-section-desc">
              Implementations MUST expose a verifier contract conforming to
              the following interface:
            </p>
            <div className="erc-code">

              <div className="erc-code-block">
                <div className="erc-code-header">
                  <div className={`erc-code-header-dot blue-dot`} />
                  <div className={`erc-code-header-dot`} />
                  <div className={`erc-code-header-dot`} />
                </div>
                <pre>{`interface IERC8150 {
  struct AgentProof {
    bytes32 mandateRoot;    // commitment to principal's mandate
    bytes32 intentHash;     // hash of this specific action intent
    bytes  zkProof;         // groth16 / plonk / halo2 proof blob
    uint64 notBefore;
    uint64 notAfter;
  }

  /// Pre-execution verification: returns true iff the proof witnesses
  /// authorization, solvency, and intent-conformance.
  function verify(AgentProof calldata p) external view returns (bool);

  /// Optional: emit a structured log for post-hoc audit.
  event Verified(bytes32 indexed mandateRoot, bytes32 indexed intentHash,
address indexed agent);
}`}</pre>
              </div>
            </div>

            <p className="erc-section-desc">The proof circuit MUST witness:</p>
            <div>
              <p className="erc-section-desc">
                <span className="dot" />membership of the agent's signing key in the principal's mandate Merkle tree;
              </p>
              <p className="erc-section-desc">
                <span className="dot" />that intentHash is within the mandate's declared action set;
              </p>
              <p className="erc-section-desc">
                <span className="dot" />a solvency commitment ≥ the payment amount, sourced from a trusted balance
                oracle;
              </p>
              <p className="erc-section-desc">
                <span className="dot" />the proof was generated inside the [notBefore, notAfter] window.
              </p>
            </div>
          </div>

          {/* 04 */}
          <div className="erc-section" id="example-flow">
            <div className="erc-section-tag">04 Example Flow</div>
            <h2 className="erc-section-title">From mandate to settled transfer.</h2>
            <ol className="erc-flow-list">
              <li className="erc-section-desc">
                <strong>1. Mandate issuance.</strong> Principal commits a mandate Merkle root on-chain; the leaves encode permitted action classes, counterparties, and limits.
              </li>
              <li className="erc-section-desc">
                <strong>2. Intent proposal.</strong> Agent composes a concrete intent (e.g. &quot;pay 200 USDC to vendor X for invoice Y&quot;) and signs it.
              </li>
              <li className="erc-section-desc">
                <strong>3. Proof generation.</strong> Agent's runtime produces a zk proof that the intent satisfies ERC-8150's three properties relative to the mandate root.
              </li>
              <li className="erc-section-desc">
                <strong>4. Verification.</strong> The settlement rail (rollup, payment processor, bank API) calls verify() before execution. Invalid proofs revert before any funds move.
              </li>
              <li className="erc-section-desc">
                <strong>5. Settlement &amp; audit.</strong> On success, the rail executes the payment and emits Verified for downstream audit tooling.
              </li>
            </ol>

            {/* Flow Diagram */}
            <div className="erc-flow-diagram">
              {[
                { label: 'Principal', sub: 'mandate', active: false },
                { label: 'Agent', sub: 'intent', active: false },
                { label: 'ERC\u20148150', sub: 'verifier', active: true },
                { label: 'Settle', sub: 'rail', active: false },
                { label: 'Audit', sub: 'log', active: false },
              ].map((node, i, arr) => (
                <div key={node.label} className="erc-flow-node-wrapper">
                  <div className={`erc-flow-node${node.active ? ' active' : ''}`}>
                    {node.active ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="101" height="116" viewBox="0 0 101 116" fill="none" className="erc-hex-bg">
                        <g clipPath="url(#paint0_diamond_1795_6111_clip_path)">
                          <g transform="matrix(2.98126e-08 0.176052 -0.292296 4.99121e-08 50.0202 1.95213e-06)">
                            <rect x="0" y="0" width="662.278" height="173.166" fill="url(#paint0_diamond_1795_6111)" opacity="0.9" shapeRendering="crispEdges"/>
                            <rect x="0" y="0" width="662.278" height="173.166" transform="scale(1 -1)" fill="url(#paint0_diamond_1795_6111)" opacity="0.9" shapeRendering="crispEdges"/>
                            <rect x="0" y="0" width="662.278" height="173.166" transform="scale(-1 1)" fill="url(#paint0_diamond_1795_6111)" opacity="0.9" shapeRendering="crispEdges"/>
                            <rect x="0" y="0" width="662.278" height="173.166" transform="scale(-1)" fill="url(#paint0_diamond_1795_6111)" opacity="0.9" shapeRendering="crispEdges"/>
                          </g>
                        </g>
                        <path d="M50.0203 0L100.041 29V87L50.0203 116L0 87V29L50.0203 0Z" fill="url(#paint0_diamond_1795_6111_fill)" opacity="0.9"/>
                        <defs>
                          <clipPath id="paint0_diamond_1795_6111_clip_path"><path d="M50.0203 0L100.041 29V87L50.0203 116L0 87V29L50.0203 0Z"/></clipPath>
                          <linearGradient id="paint0_diamond_1795_6111" x1="0" y1="0" x2="500" y2="500" gradientUnits="userSpaceOnUse">
                            <stop offset="0.259615" stopColor="#655ADA"/>
                            <stop offset="1" stopColor="#FFE3A3" stopOpacity="0.8"/>
                          </linearGradient>
                          <linearGradient id="paint0_diamond_1795_6111_fill" x1="50" y1="0" x2="50" y2="116" gradientUnits="userSpaceOnUse">
                            <stop offset="0" stopColor="#655ADA"/>
                            <stop offset="1" stopColor="#A89BEE" stopOpacity="0.8"/>
                          </linearGradient>
                        </defs>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="101" height="116" viewBox="0 0 101 116" fill="none" className="erc-hex-bg">
                        <path d="M50.4148 0.453857L100.435 29.1113V86.4262L50.4148 115.084L0.394531 86.4262V29.1113L50.4148 0.453857Z" stroke="white" strokeOpacity="0.8" strokeWidth="0.787721"/>
                      </svg>
                    )}
                    <div className="erc-flow-node-text">
                      <span className="erc-flow-node-label">{node.label}</span>
                      <span className="erc-flow-node-sub">{node.sub}</span>
                    </div>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="erc-flow-arrow">
                      <span className="erc-flow-arrow-text">{i === 0 ? 'commit' : i === 1 ? 'sign + prove' : i === 2 ? 'verify' : 'execute'}</span>
                      <svg width="40" height="8" viewBox="0 0 40 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 4H37M37 4L33 1M37 4L33 7" stroke="rgba(255,255,255,0.4)" strokeWidth="1"/>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 05 Rationale */}
          <div className="erc-section" id="rationale">
            <div className="erc-section-tag">05 Rationale</div>
            <h2 className="erc-section-title">Why a new ERC.</h2>
            <p className="erc-section-desc">
              Existing standards (EIP-712 typed signatures, EIP-7702 account
              abstraction, EIP-4337 user operations) handle who signed a
              message and how it is dispatched. None of them answer the
              question{' '}
              <strong className="erc-highlight">"should this have happened at all?"</strong>
              {' '}before settlement.
            </p>
            <p className="erc-section-desc">
              ERC-8150 is deliberately minimal: a single <span className={`font-romo`}>verify()</span>
              &nbsp;call, a
              canonical proof struct, and a simple event. It is designed to be
              wrapped by higher-level systems - agent registries, circuit
              libraries, rollup pre-confirmers - without prescribing their
              internals.
            </p>
          </div>

          {/* 06 Security Considerations */}
          <div className="erc-section" id="security">
            <div className="erc-section-tag">06 Security Considerations</div>
            <h2 className="erc-section-title">What the proof does not say.</h2>
            <p className="erc-section-desc">
              A valid ERC-8150 proof attests to authorization, solvency, and
              intent-conformance at{' '}
              <strong>proof time</strong>. It does not attest to the
              correctness of the underlying oracle, the mandate's suitability for
              the agent's actual purpose, or the behavior of the rail after
              verification. Implementers SHOULD treat the proof as a necessary
              but not sufficient condition, and pair it with rail-side enforcement of
              the <span className={`font-romo`}>notAfter</span> window.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ERC;
